import type { AdminCoverageSection, TeamMember } from '../domain/types';

export interface DoorCreditParticipant {
  name: string;
  personId?: string;
}

export interface CompletedSectionCreditAudit {
  neighborhoodName: string;
  sectionNumber: number;
  doors: number;
  participants: DoorCreditParticipant[];
  sharePerParticipant: number;
  source: 'RECORDED_TEAM' | 'ASSIGNEE_FALLBACK' | 'MISSING_ROSTER' | 'NO_CREDIT';
}

/** The recorded completion roster is authoritative; an unrecorded roster falls back to the direct assignee. */
export function participantsForCompletedSection(section: AdminCoverageSection): DoorCreditParticipant[] {
  const recorded = uniqueParticipants(section.teamMembers);
  if (recorded.length) return recorded;
  if (section.completionRosterExplicit) return []; // Admin chose completion without personal credit.
  if (section.assignedTo?.displayName.trim()) {
    return [{ name: section.assignedTo.displayName.trim(), personId: section.assignedTo.id }];
  }
  return [];
}

export function doorCreditForPerson(section: AdminCoverageSection, personId: string, personName: string): number {
  if (section.status !== 'COMPLETE') return 0;
  return sectionDoorCreditForPerson(section, personId, personName);
}

export function sectionDoorCreditForPerson(section: AdminCoverageSection, personId: string, personName: string): number {
  const participants = participantsForCompletedSection(section);
  if (!participants.length) return 0;
  const included = participants.some((person) => person.personId
    ? person.personId === personId
    : normalizeName(person.name) === normalizeName(personName));
  return included ? section.doors / participants.length : 0;
}

export function auditCompletedSectionCredits(sections: AdminCoverageSection[]): CompletedSectionCreditAudit[] {
  return sections
    .filter((section) => section.status === 'COMPLETE')
    .map((section) => {
      const recorded = uniqueParticipants(section.teamMembers);
      const participants = recorded.length ? recorded : participantsForCompletedSection(section);
      const source: CompletedSectionCreditAudit['source'] = recorded.length
        ? 'RECORDED_TEAM'
        : section.completionRosterExplicit
          ? 'NO_CREDIT'
          : section.assignedTo
          ? 'ASSIGNEE_FALLBACK'
          : 'MISSING_ROSTER';
      return {
        neighborhoodName: section.neighborhoodName,
        sectionNumber: section.sectionNumber,
        doors: section.doors,
        participants,
        sharePerParticipant: participants.length ? section.doors / participants.length : 0,
        source
      };
    })
    .sort((left, right) => left.neighborhoodName.localeCompare(right.neighborhoodName) || left.sectionNumber - right.sectionNumber);
}

function uniqueParticipants(members: TeamMember[]): DoorCreditParticipant[] {
  const unique = new Map<string, DoorCreditParticipant>();
  for (const member of members) {
    const name = member.name.trim().replace(/\s+/g, ' ');
    const normalized = normalizeName(name);
    if (!normalized) continue;
    const personId = member.personId?.trim() || undefined;
    const key = personId ? `id:${personId}` : `name:${normalized}`;
    if (!unique.has(key)) unique.set(key, { name, ...(personId ? { personId } : {}) });
  }
  return [...unique.values()];
}

function normalizeName(value: string): string {
  return value.trim().replace(/\s+/g, ' ').toLocaleLowerCase();
}
